import React, { useState, useRef, useEffect } from "react";
import { Container, Nav, Navbar, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import xtImage from "../../assests/main-logo.svg"
import ServicePopup from "../ServicePopup/ServicePopup";
import ContactFormModal from "../header/ContactFormModal";
import "./Header.css";

const Header: React.FC = () => {
  const [showServices, setShowServices] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setShowServices(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll helper
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = isMobile ? 100 : 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  // Handle navigation clicks
  const handleNavClick = (e: React.MouseEvent<HTMLElement>, sectionId: string) => {
    e.preventDefault();

    if (location.pathname !== "/") {
      // Navigate to home and pass sectionId in state
      navigate("/", { state: { targetId: sectionId } });
    } else {
      scrollToSection(sectionId);
    }

    // Close menus
    setShowServices(false);
    setExpanded(false);
    setMobileActiveTab(null);
  };

  // Detect scroll for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle logo click → go home
  const handleLogoClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/", { state: { targetId: "home" } });
    } else {
      scrollToSection("home");
    }

    setExpanded(false);
    setMobileActiveTab(null);
  };

  // When returning to "/" with a targetId in state → scroll
  useEffect(() => {
    if (location.pathname === "/" && (location.state as any)?.targetId) {
      const sectionId = (location.state as any).targetId;
      setTimeout(() => scrollToSection(sectionId), 200); // wait for DOM
    }
  }, [location]);

  return (
    <>
      <Navbar
        expand="lg"
        sticky="top"
        variant="light"
        className={`nav-body ${scrolled ? "scrolled" : ""}`}
        expanded={expanded}
      >
        <Container fluid className="header-container">
          <Navbar.Brand href="#home" className="navbar-brand-custom" onClick={handleLogoClick}>
            <img className="logo-img" src={xtImage} alt="XYLOITE Technologies" />
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            className="custom-toggler"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <span className="close-icon">×</span> : <span className="navbar-toggler-icon"></span>}
          </Navbar.Toggle>

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="nav-links-container">
              <Nav.Link
                href="#home"
                onClick={(e) => handleNavClick(e, "home")}
                className="nav-link-custom"
              >
                Home
              </Nav.Link>

              <Nav.Link
                href="#about"
                onClick={(e) => handleNavClick(e, "about")}
                className="nav-link-custom"
              >
                About Us
              </Nav.Link>

              <div
                className="services-dropdown-container"
                ref={servicesRef}
                {...(!isMobile && {
                  onMouseEnter: () => setShowServices(true),
                  onMouseLeave: () => setShowServices(false),
                })}
              >
                <Nav.Link
                  href="#services"
                  as="span"
                  className="services-nav-link"
                  onClick={() =>
                    isMobile && setMobileActiveTab((prev) => (prev === "services" ? null : "services"))
                  }
                >
                  Services
                  <span className={`dropdown-arrow ${mobileActiveTab === "services" || showServices ? "open" : ""}`}>
                    <svg
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Nav.Link>

                {(showServices || mobileActiveTab === "services") && (
                  <div className="services-dropdown-popup">
                    {isMobile && (
                      <div className="mobile-close-header">
                        <h2 className="popup-title">Services</h2>
                        <button className="mobile-close-btn" onClick={() => setMobileActiveTab(null)}>
                          ×
                        </button>
                      </div>
                    )}
                    <ServicePopup
                      onclickservice={() => setExpanded(!expanded)}
                      onClose={() => {
                        setMobileActiveTab(null);
                        setShowServices(false);
                      }}
                    />
                  </div>
                )}
              </div>

              <Nav.Link
                href="#product"
                onClick={(e) => handleNavClick(e, "product")}
                className="nav-link-custom"
              >
                Product
              </Nav.Link>

              <Nav.Link
                href="#career"
                onClick={(e) => handleNavClick(e, "career")}
                className="nav-link-custom"
              >
                Career
              </Nav.Link>
            </Nav>

            <div className="button-herader">
              <Button
                variant="outline"
                className="read-more"
                onClick={() => {
                  navigate("/Contact-Us");
                  setExpanded(false);
                  setMobileActiveTab(null);
                }}
              >
                Contact us
              </Button>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Contact Form Modal */}
      {showContactForm && <ContactFormModal onClose={() => setShowContactForm(false)} />}
    </>
  );
};

export default Header;
